import React from "react";

// Maintenance progress tracker — 5-step pipeline that mirrors the
// `mts.st` + `mts.sp_status` state machine. Shared by tenant
// (TenMaintList) and landlord (MaintPage) views so both parties
// see the SAME job lifecycle in the SAME visual treatment.
//
// State → step index mapping:
//   0 Pending     — st === "open" (tenant submitted, no SP yet)
//   1 Assigned    — st === "assigned", sp_status === "pending"
//   2 Accepted    — st === "assigned", sp_status === "accepted"
//   3 Started     — st === "assigned", sp_status === "in_progress"
//                   OR sp_status === "completed" (waiting for tenant
//                   confirmation; not yet "Resolved" until landlord
//                   marks st="done" or tenant confirms via token)
//   4 Resolved    — st === "done"
//
// Visual language (inspired by the premium tier-progress aesthetic
// — gold/star "achievement" pattern translated to our brand colors):
//   - Future step:   hollow circle on muted grey ring
//   - Active step:   filled accent circle with inner white pulse +
//                    soft outer glow ring (mobile-friendly affordance)
//   - Done step:     filled green circle with white check
//   - Final reached: filled green circle with white STAR (celebrates
//                    completion the way the reference design does)
//   - Connector:     3px rounded rail, fills in accent until done,
//                    flips to green only when fully Resolved
//
// Each step has a short sub-label under the primary one ("Submitted"
// / "SP picked" / etc.) so the pipeline reads as plain English
// without the user having to map the enum to meaning.
//
// Props:
//   st         maintenance.st ("open" | "assigned" | "done")
//   sp_status  service-person job status (optional)
//   h          theme object from AppContext

const STEPS = [
    { label: "Pending",  sub: "Submitted" },
    { label: "Assigned", sub: "SP picked" },
    { label: "Accepted", sub: "Confirmed" },
    { label: "Started",  sub: "In progress" },
    { label: "Resolved", sub: "Complete" },
];

export default function StatusTracker({ st, sp_status, h }) {
    const ac = h.ac || "#3b82f6";
    const gn = h.gn || "#16a34a";
    const bd = h.bd || "rgba(0,0,0,0.08)";
    const sc = h.sc || "#8e8e93";
    const cd = h.cd || "#ffffff";

    let stepIdx = 0;
    if (st === "done") stepIdx = 4;
    else if (st === "assigned") {
        if (sp_status === "in_progress" || sp_status === "completed") stepIdx = 3;
        else if (sp_status === "accepted") stepIdx = 2;
        else stepIdx = 1;
    }
    const last = STEPS.length - 1;
    const isComplete = stepIdx === last;

    // Connector fill: 78% rail (11% inset each side so the dots don't
    // crowd the card edges and labels can center under them).
    const fillPct = (stepIdx / last) * 78;
    // Sit the rail vertically centered through the 24px circles.
    // padding-top of the row is 8 → circle top = 8 → circle center = 8 + 12 = 20.
    const railTop = 20;

    return (
        <div style={{
            position: "relative",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginTop: 14,
            padding: "8px 4px 4px",
        }}>
            {/* Background rail — full-width muted track */}
            <div style={{
                position: "absolute",
                top: railTop, left: "11%", right: "11%",
                height: 3, background: bd,
                borderRadius: 2,
                zIndex: 0,
            }} />
            {/* Progress rail — turns green only at the final Resolved step */}
            <div style={{
                position: "absolute",
                top: railTop, left: "11%",
                width: `${fillPct}%`, height: 3,
                background: isComplete ? gn : ac,
                borderRadius: 2,
                zIndex: 1,
                transition: "width 0.4s ease, background 0.3s ease",
            }} />

            {STEPS.map(({ label, sub }, i) => {
                const isDone = i < stepIdx;
                const isActive = i === stepIdx;
                const isFuture = i > stepIdx;
                const isFinalActive = isComplete && isActive;
                const isGreen = isDone || isFinalActive;

                // Color the dot + labels per state. Future steps stay
                // muted so the eye lands on the active one immediately.
                const dotFill   = isGreen ? gn : isActive ? ac : cd;
                const ringColor = isGreen ? gn : isActive ? ac : bd;
                const labelColor = isGreen ? gn : isActive ? ac : sc;

                return (
                    <div key={label} style={{
                        position: "relative",
                        zIndex: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 5,
                        flex: 1,
                    }}>
                        {/* Step circle — 24px so it's tappable-feeling
                            and the icon inside reads clearly on mobile. */}
                        <div style={{
                            width: 24, height: 24,
                            borderRadius: "50%",
                            background: dotFill,
                            border: `2px solid ${ringColor}`,
                            // Active step gets a soft outer glow + shadow
                            // so the user knows where they are at a glance.
                            boxShadow: isActive
                                ? `0 0 0 4px ${(isFinalActive ? gn : ac)}1f, 0 2px 6px ${(isFinalActive ? gn : ac)}30`
                                : "none",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.3s ease",
                        }}>
                            {isDone && (
                                // Past step → white check
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            )}
                            {isFinalActive && (
                                // Goal reached → star (celebration moment,
                                // mirrors the reference design's "Top" icon).
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff" stroke="#fff" strokeWidth="1" strokeLinejoin="round">
                                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26" />
                                </svg>
                            )}
                            {isActive && !isFinalActive && (
                                // Current (non-final) step → inner white pulse
                                // dot so it reads as "you are here".
                                <div style={{
                                    width: 8, height: 8, borderRadius: 4,
                                    background: "#fff",
                                }} />
                            )}
                            {/* Future steps render an empty circle — the white
                                bg + muted ring is the visual cue on its own. */}
                        </div>

                        {/* Primary label — uppercase, bold for active, dimmer for future */}
                        <span style={{
                            fontSize: 9.5,
                            fontWeight: isActive ? 700 : isDone ? 600 : 500,
                            color: labelColor,
                            letterSpacing: 0.4,
                            textTransform: "uppercase",
                            whiteSpace: "nowrap",
                            lineHeight: 1.2,
                            marginTop: 1,
                        }}>
                            {label}
                        </span>

                        {/* Sub-label — plain English hint. Faded for future
                            steps so the cognitive weight stays on done + now. */}
                        <span style={{
                            fontSize: 8,
                            fontWeight: 500,
                            color: isFuture ? sc : labelColor,
                            letterSpacing: 0.1,
                            whiteSpace: "nowrap",
                            opacity: isFuture ? 0.55 : 0.85,
                            marginTop: -2,
                            lineHeight: 1.2,
                        }}>
                            {sub}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}
